/**
 * Supplier-side Stock Planning grid.
 *
 * Mirrors the customer StockPlanningTab but flips the role: the supplier picks
 * a CUSTOMER from the dropdown, and the same stock-planning endpoint is called
 * with `customer_id`. The backend (`StockPlanningViewSet.list`) detects the
 * supplier role and treats the caller's company as the supplier in the lookup.
 *
 * Editable qty (item 15) and bespoke columns (Remaining-to-produce / Fulfilment %)
 * are deferred — track in the wishlist for follow-up.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { callOffAPI, coreAPI, Company } from '../../../lib/api';
import type { StockPlanningItem, ColumnPreference } from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Pagination } from '../../ui/pagination';
import StockPlanningTable from './StockPlanningTable';
import ColumnManager from './ColumnManager';
import ProposePartChangeModal from './ProposePartChangeModal';
import PartChangeHistoryDrawer from './PartChangeHistoryDrawer';
import { useCallOffConfig } from '../../../hooks/useCallOffConfig';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import PartSearchInput from '../shared/PartSearchInput';
import { toast } from 'sonner';

/**
 * Per-browser persistence key for the supplier stock-planning column
 * preferences (visibility + order). Customer-side prefs live server-side
 * in StockPlanningColumnPreference; supplier prefs are local-only for now
 * to avoid a schema change. Bump the suffix if the prefs shape changes.
 */
const SUPPLIER_PREFS_KEY = 'helix.supplier_stock_planning.columns.v1';

interface SupplierColumnPrefs {
  /** column_ids the supplier has hidden via the Columns dropdown. */
  hidden: string[];
  /** Explicit order; column_ids missing here fall back to SUPPLIER_COLUMNS order. */
  order: string[];
}

const loadSupplierPrefs = (): SupplierColumnPrefs => {
  try {
    const raw = localStorage.getItem(SUPPLIER_PREFS_KEY);
    if (!raw) return { hidden: [], order: [] };
    const parsed = JSON.parse(raw) as Partial<SupplierColumnPrefs>;
    return {
      hidden: Array.isArray(parsed?.hidden) ? parsed.hidden.filter((x) => typeof x === 'string') : [],
      order: Array.isArray(parsed?.order) ? parsed.order.filter((x) => typeof x === 'string') : [],
    };
  } catch {
    return { hidden: [], order: [] };
  }
};

/**
 * Curated column set for the supplier grid. Suppliers see only the
 * production-relevant columns regardless of any per-user preferences.
 * Order here is the displayed left-to-right order.
 * CR-8624: the API returns and exports only these to a supplier — keep in
 * step with `SUPPLIER_COLUMNS` in `call_off/services/stock_planning_visibility.py`.
 */
const SUPPLIER_COLUMNS: string[] = [
  'part_number',
  'description',
  'pallet_qty',
  'load_qty',
  'min_stock',
  // Two separate views of the supplier's PO commitment:
  //   * "Ordered Overall" (ordered_by_supplier) — the gross open PO qty
  //     for this supplier. Drives Fulfilment % too.
  //   * "Remaining to Produce" — gross minus stock already booked in.
  //     Goes blank when ≤ 0 (nothing left to make).
  'ordered_by_supplier',
  'remaining_to_produce',
  'stock_with_supplier',
  'call_off_requested_qty',
  'sum_on_saltney',
  'sum_on_mold',
  'sum_on_direct',
  // CR-6179: "In Transit" removed from the supplier grid.
  'fulfilment_pct',
];

export default function SupplierStockPlanningTab() {
  const { config } = useCallOffConfig();

  // CR-6176 / CR-1154: open the SO/PO Lines tab pre-filtered to this part number
  // in a NEW TAB, so the supplier can compare stock planning and the part's
  // order lines side by side. The PO Lines tab reads `partNumber` from the URL
  // on mount (see usePOLinesState); a fresh tab recovers the session via the
  // CR-6282 BroadcastChannel handoff.
  const handlePartNumberClick = (partNumber: string) => {
    const params = new URLSearchParams({ tab: 'po_lines', partNumber });
    window.open(`/purchasing?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };
  const [customers, setCustomers] = useState<Company[]>([]);
  const [customerId, setCustomerId] = useState<string>('');
  const [items, setItems] = useState<StockPlanningItem[]>([]);
  const [allColumns, setAllColumns] = useState<ColumnPreference[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // CR-7717: part number / description search, run server-side like the
  // customer grid so the count and pages cover every match.
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const [proposeTarget, setProposeTarget] = useState<StockPlanningItem | null>(null);
  const [historyTarget, setHistoryTarget] = useState<StockPlanningItem | null>(null);
  const [exporting, setExporting] = useState(false);
  const [columnPrefs, setColumnPrefs] = useState<SupplierColumnPrefs>(loadSupplierPrefs);

  // Persist column prefs on every change (per-browser, per-user via localStorage).
  useEffect(() => {
    try {
      localStorage.setItem(SUPPLIER_PREFS_KEY, JSON.stringify(columnPrefs));
    } catch {
      // Storage full / disabled — ignore; the in-memory state still works.
    }
  }, [columnPrefs]);

  useEffect(() => {
    coreAPI.getAccessibleCompanies({ context: 'customer_filter' })
      .then((res) => {
        const list = res.data.results || [];
        setCustomers(list);
        if (list.length === 1) setCustomerId(String(list[0].id));
      })
      .catch(() => toast.error('Failed to load customers'));
  }, []);

  const fetchData = useCallback(async () => {
    if (!customerId) {
      setItems([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page,
        page_size: pageSize,
        customer_id: customerId,
      };
      if (debouncedSearch) params['search'] = debouncedSearch;
      const [dataRes, colRes] = await Promise.all([
        callOffAPI.getStockPlanning(params),
        callOffAPI.getColumns(),
      ]);
      setItems(dataRes.data.items);
      setTotalCount(dataRes.data.total_count);
      setAllColumns(colRes.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load stock planning data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, customerId, debouncedSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build the supplier column list from the curated SUPPLIER_COLUMNS,
  // pulling labels from the API response and applying local prefs:
  //   * Order: prefs.order first (filtered to known supplier columns), then
  //     any default columns the user hasn't reordered yet. Stable across
  //     SUPPLIER_COLUMNS additions.
  //   * Visibility: column ids in prefs.hidden render with is_visible=false
  //     so the manager dropdown shows them but the table skips them.
  // Saltney/Mold labels come from the customer's CallOffCustomerConfig so
  // admin renames flow through. The secondary (Mold) column is hidden when
  // the customer hasn't configured one — that's a config-driven hide, not
  // a user pref.
  const allSupplierColumns: ColumnPreference[] = useMemo(() => {
    const byId = new Map(allColumns.map((c) => [c.column_id, c]));
    const labelOverrides: Record<string, string> = {
      sum_on_saltney: config.primary_warehouse_column_label || 'Sum On Saltney',
      sum_on_mold: config.secondary_warehouse_column_label || 'Sum On Mold',
      // Ordered Overall = gross open PO qty for this supplier (drives the
      // Fulfilment % denominator). Remaining to Produce = gross − stock
      // already booked in; blanks when ≤ 0 (handled in StockPlanningTable).
      ordered_by_supplier: 'Ordered Overall',
      remaining_to_produce: 'Remaining to Produce',
      call_off_requested_qty: 'Call Off Requested Qty',
    };
    const includeMold = !!(config.secondary_warehouse_label || '').trim();
    const allowed = SUPPLIER_COLUMNS.filter((id) => id !== 'sum_on_mold' || includeMold);

    const orderedFromPrefs = columnPrefs.order.filter((id) => allowed.includes(id));
    const remaining = allowed.filter((id) => !orderedFromPrefs.includes(id));
    const finalOrder = [...orderedFromPrefs, ...remaining];

    return finalOrder.map((id, idx) => {
      const existing = byId.get(id);
      const label = labelOverrides[id] ?? existing?.label ?? id;
      const is_visible = !columnPrefs.hidden.includes(id);
      if (existing) return { ...existing, label, is_visible, sort_order: idx };
      return { id: -idx, column_id: id, label, sort_order: idx, is_visible } as ColumnPreference;
    });
  }, [allColumns, config, columnPrefs]);

  // Table only renders the visible subset, in the same order.
  const visibleColumns = useMemo(
    () => allSupplierColumns.filter((c) => c.is_visible),
    [allSupplierColumns],
  );

  const handleToggleColumn = useCallback((columnId: string) => {
    setColumnPrefs((prev) => {
      const isHidden = prev.hidden.includes(columnId);
      return {
        ...prev,
        hidden: isHidden ? prev.hidden.filter((id) => id !== columnId) : [...prev.hidden, columnId],
      };
    });
  }, []);

  const handleSortColumns = useCallback(
    (updated: { column_id: string; sort_order: number }[]) => {
      setColumnPrefs((prev) => ({
        ...prev,
        order: [...updated]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((c) => c.column_id),
      }));
    },
    [],
  );

  const handleExport = useCallback(
    async (file_format: 'csv' | 'xlsx') => {
      if (!customerId) {
        toast.error('Pick a customer before exporting.');
        return;
      }
      setExporting(true);
      try {
        // Send the *currently visible* columns in display order so the
        // file matches what the supplier sees on screen — including any
        // hide/reorder choices made via the Columns dropdown.
        const columns = visibleColumns.map((c) => c.column_id).join(',');
        const response = await callOffAPI.exportStockPlanning({
          file_format,
          customer_id: Number(customerId),
          columns,
          search: debouncedSearch || undefined,
        });
        const mime =
          file_format === 'csv'
            ? 'text/csv'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const blob = new Blob([response.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stock_planning_${new Date().toISOString().split('T')[0]}.${file_format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${file_format.toUpperCase()}`);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Export failed';
        toast.error(message);
      } finally {
        setExporting(false);
      }
    },
    [customerId, visibleColumns, debouncedSearch],
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Customer</label>
          <select
            value={customerId}
            onChange={(e) => { setCustomerId(e.target.value); setPage(1); }}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          >
            <option value="">Select a customer…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <PartSearchInput
            value={search}
            onChange={(v) => { setSearch(v); setPage(1); }}
            className="w-72"
          />
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" disabled={!customerId || exporting}>
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-700" />
                Download Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileText className="h-4 w-4 mr-2 text-blue-700" />
                Download CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ColumnManager
            columns={allSupplierColumns}
            onToggle={handleToggleColumn}
            onSort={handleSortColumns}
          />
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Stock Planning Grid
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({totalCount} parts)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!customerId ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              Select a customer to view stock planning data
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : debouncedSearch && items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No parts match &ldquo;{debouncedSearch}&rdquo;.
            </div>
          ) : (
            <StockPlanningTable
              items={items}
              columns={visibleColumns}
              onProposeChange={setProposeTarget}
              onViewHistory={setHistoryTarget}
              onPartNumberClick={handlePartNumberClick}
              zeroOrderedWhenProduced
            />
          )}

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            itemLabel="parts"
          />
        </CardContent>
      </Card>

      {proposeTarget && customerId && (
        <ProposePartChangeModal
          open={!!proposeTarget}
          onOpenChange={(open) => { if (!open) setProposeTarget(null); }}
          partNumber={proposeTarget.part_number}
          partDescription={proposeTarget.description}
          customerId={Number(customerId)}
          onSubmitted={fetchData}
        />
      )}

      {historyTarget && customerId && (
        <PartChangeHistoryDrawer
          open={!!historyTarget}
          onOpenChange={(open) => { if (!open) setHistoryTarget(null); }}
          partNumber={historyTarget.part_number}
          partDescription={historyTarget.description}
          customerId={Number(customerId)}
        />
      )}
    </div>
  );
}
